import React, { memo, useMemo } from "react";
import Chart from "react-apexcharts";

type LineGraphProps = {
  labels: string[];
  values: number[];
  graphColorRgb?: string;
  height?: string;
};
export const LineGraph: React.FC<LineGraphProps> = memo(({ labels, values, graphColorRgb = '54,121,254', height = '500' }) => {
  // Defensive resolve for Chart component to avoid undefined element type at runtime
  const ChartComponent: any = (Chart as any)?.default ?? (Chart as any);

  // Normalize inputs
  const rawValues = useMemo(() => (Array.isArray(values) ? values : []), [values]);
  const safeValues = useMemo(() => rawValues.map((n) => (typeof n === 'number' && isFinite(n) ? n : 0)), [rawValues]);
  const rawLabels = useMemo(() => (Array.isArray(labels) ? labels.map((l:any) => String(l)) : []), [labels]);
  const minLen = Math.min(safeValues.length, rawLabels.length);
  const alignedValues = useMemo(() => safeValues.slice(0, minLen), [safeValues, minLen]);
  const alignedLabels = useMemo(() => rawLabels.slice(0, minLen), [rawLabels, minLen]);
  const hasAnyPoint = alignedValues.length > 0 && alignedValues.some((v) => v !== 0);
  // Key depends only on data shape to remount chart safely when shape changes
  const chartKey = useMemo(() => `${alignedLabels.length}-${alignedValues.length}`,[alignedLabels.length, alignedValues.length]);

  const options = useMemo(() => ({
    chart: {
      id: "basic-area",
      animations: { enabled: false },
      toolbar: {
        show: false
      }
    },
    xaxis: {
      categories: alignedLabels,
      labels: {
        style: {
          fontSize: '10px'
        },
        rotate: 0,
        rotateAlways: false
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      forceNiceScale: true,
      labels: {
        style: {
          fontSize: '10px'
        },
      },
    },
    stroke: {
      curve: 'smooth' as 'smooth',
      colors: [`rgb(${graphColorRgb})`],
      width: 2
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.9,
        stops: [0, 90, 100],
        colorStops: [
          {
            offset: 0,
            color: `rgb(${graphColorRgb})`,
            opacity: 0.4
          },
          {
            offset: 100,
            color: `rgb(${graphColorRgb})`,
            opacity: 0
          },
        ]
      }
    },
    markers: {
      size: 0,
      hover: {
        size: 4
      },
      colors: [`rgb(${graphColorRgb})`]
    },
    tooltip: {
      theme: "dark",
      marker: {
        show: true,
      },
    },
    legend: {
      show: false
    },
    dataLabels: {
      enabled: false
    }
  }), [alignedLabels, graphColorRgb]);

  const series = useMemo(() => ([
    {
      name: '',
      data: alignedValues,
      color: `rgba(${graphColorRgb},.9)`
    }
  ]), [alignedValues, graphColorRgb]);

  // Ensure the resolved value is a valid React component (function/class)
  if (!ChartComponent || typeof ChartComponent !== 'function') {
    return null;
  }

  if (!hasAnyPoint) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
        データがありません
      </div>
    );
  }

  // Force remount when data shape changes to avoid Apex internal stale refs
  return <ChartComponent key={chartKey} options={options} series={series} type="area" height={height} />;
});